import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { cn } from "@/lib/utils";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { servicesCopy } from "@/lib/siteCopy";

export type SolveCard = {
  name: string;
  image?: string;
  tone: string;
  /** Hex used by hero animation theming */
  color: string;
  nameClass: string;
  description: string;
  highlights: string[];
};

/** Shared palette - hero morph reads these tones too */
export const solveCards: SolveCard[] = [
  {
    name: "Web Apps",
    image: "/whatwesolve/web.png",
    tone: "bg-[#F3EFEA]",
    color: "#F3EFEA",
    nameClass: "text-neutral-900",
    description:
      "Product-grade web applications with clean architecture, fast load times, and interfaces your users actually enjoy.",
    highlights: [
      "Responsive product UI",
      "Auth, dashboards & workflows",
      "Performance-first delivery",
    ],
  },
  {
    name: "Cloud",
    image: "/whatwesolve/cloud.png",
    tone: "bg-[#1A1A1A]",
    color: "#1A1A1A",
    nameClass: "text-white",
    description:
      "Reliable cloud foundations so you can ship features without babysitting infra, scaling, or deploy chaos.",
    highlights: [
      "Scalable cloud architecture",
      "CI/CD & environment parity",
      "Observability baked in",
    ],
  },
  {
    name: "Mobile",
    image: "/whatwesolve/mobile.png",
    tone: "bg-[#E8EEF5]",
    color: "#E8EEF5",
    nameClass: "text-neutral-900",
    description:
      "Native-feel mobile experiences that stay consistent with your web product and ship on a shared engineering rhythm.",
    highlights: [
      "Cross-platform apps",
      "Shared design language",
      "Store-ready releases",
    ],
  },
  {
    name: "APIs",
    image: "/whatwesolve/api.png",
    tone: "bg-[#E7F0EC]",
    color: "#E7F0EC",
    nameClass: "text-neutral-900",
    description:
      "Stable APIs and integrations that connect your stack cleanly - documented, versioned, and built for real traffic.",
    highlights: [
      "REST & event-driven APIs",
      "Third-party integrations",
      "Clear contracts & docs",
    ],
  },
  {
    name: "AI",
    image: "/whatwesolve/ai.png",
    tone: "bg-[#F2E8F8]",
    color: "#F2E8F8",
    nameClass: "text-neutral-900",
    description:
      "Practical AI features embedded into your product - assistants, automation, and insights that reduce busywork.",
    highlights: [
      "LLM-powered workflows",
      "Secure data handling",
      "Measurable product outcomes",
    ],
  },
  {
    name: "Support",
    image: "/whatwesolve/support.png",
    tone: "bg-[#FFF4DE]",
    color: "#FFF4DE",
    nameClass: "text-neutral-900",
    description:
      "Ongoing partnership after launch - maintenance, iteration, and a team that stays accountable when production matters.",
    highlights: [
      "Post-launch ownership",
      "Priority issue response",
      "Continuous improvements",
    ],
  },
];

const EDGE_PAD =
  "pl-5 pr-5 sm:pl-6 sm:pr-6 lg:pl-[max(2.5rem,calc((100vw-1400px)/2+2.5rem))] lg:pr-[max(2.5rem,calc((100vw-1400px)/2+2.5rem))]";

export function Services() {
  const lang = useSiteLanguage();
  const t = servicesCopy[lang];
  const [emblaRef, emblaApi] = useEmblaCarousel({
    // First card flush to left content pad; later snaps leave ~½ card peeking on the left.
    // Matching right pad mirrors that inset on the final snap without killing mid-swipe peeks.
    align: (viewSize, snapSize, index) =>
      index === 0 ? 0 : Math.min(snapSize * 0.5, viewSize * 0.18),
    containScroll: "trimSnaps",
    dragFree: false,
    skipSnaps: false,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const localizedCards = useMemo(
    () =>
      solveCards.map((card, i) => ({
        ...card,
        name: t.cards[i]?.name ?? card.name,
        description: t.cards[i]?.description ?? card.description,
        highlights: [...(t.cards[i]?.highlights ?? card.highlights)],
      })),
    [t],
  );

  const activeCard =
    activeIndex !== null ? localizedCards[activeIndex] ?? null : null;

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
    emblaApi.on("settle", onSelect);
    return () => {
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
      emblaApi.off("settle", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollNext = useCallback(() => {
    if (!emblaApi || !emblaApi.canScrollNext()) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi || !emblaApi.canScrollPrev()) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  return (
    <section
      id="services"
      className="bg-background pt-16 pb-10 sm:pt-20 sm:pb-12 lg:pt-24 lg:pb-14"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-6 lg:px-10">
        <div className="mb-8 max-w-3xl sm:mb-10 lg:mb-12">
          <h2 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            {t.title}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground sm:text-xl">
            {t.subtitle}
          </p>
        </div>
      </div>

      {/* Equal edge pads: last snap mirrors first; mid-swipe still peeks half cards */}
      <div className="relative">
        <div ref={emblaRef} className="overflow-hidden">
          <div className={cn("flex gap-4 sm:gap-5", EDGE_PAD)}>
            {localizedCards.map((card, index) => (
              <article
                key={solveCards[index]!.name}
                className={cn(
                  "solve-card min-w-0 shrink-0 grow-0",
                  "basis-[78%] sm:basis-[48%] md:basis-[36%] lg:basis-auto",
                )}
              >
                <button
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={cn(
                    "relative aspect-[3/4] w-full overflow-hidden rounded-[1.75rem] text-left transition-transform active:scale-[0.985] sm:rounded-[2rem]",
                    !card.image && card.tone,
                  )}
                  aria-label={`${t.learnMore} ${card.name}`}
                >
                  {card.image ? (
                    <img
                      src={card.image}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <div
                      className="pointer-events-none absolute inset-0 opacity-[0.35]"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.08) 1px, transparent 0)",
                        backgroundSize: "18px 18px",
                      }}
                      aria-hidden
                    />
                  )}

                  {card.image && (
                    <div
                      className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent"
                      aria-hidden
                    />
                  )}

                  <p
                    className={cn(
                      "absolute bottom-5 left-5 text-[1.05rem] font-semibold tracking-[-0.02em] sm:bottom-6 sm:left-6 sm:text-lg",
                      card.image ? "text-white" : card.nameClass,
                    )}
                  >
                    {card.name}
                  </p>
                </button>
              </article>
            ))}
          </div>
        </div>

        <div className="mx-auto mt-6 flex max-w-[1400px] items-center justify-end gap-2.5 px-5 sm:mt-8 sm:px-6 lg:px-10">
          <button
            type="button"
            aria-label="Previous"
            disabled={!canPrev}
            onClick={scrollPrev}
            className={cn(
              "inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/15 bg-white text-neutral-800 transition-colors dark:border-white/20 dark:bg-transparent dark:text-white",
              canPrev
                ? "hover:bg-neutral-50 dark:hover:bg-white/5"
                : "cursor-default opacity-35",
            )}
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            aria-label="Next"
            disabled={!canNext}
            onClick={scrollNext}
            className={cn(
              "inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/15 bg-white text-neutral-800 transition-colors dark:border-white/20 dark:bg-transparent dark:text-white",
              canNext
                ? "hover:bg-neutral-50 dark:hover:bg-white/5"
                : "cursor-default opacity-35",
            )}
          >
            <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <Drawer
        open={activeIndex !== null}
        onOpenChange={(open) => {
          if (!open) setActiveIndex(null);
        }}
      >
        <DrawerContent className="max-h-[90vh] overflow-hidden md:max-h-[85vh]">
          {activeCard && (
            <div className="relative mx-auto w-full max-w-5xl">
              <DrawerClose
                className="absolute right-3 top-2 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-sm transition-colors hover:bg-muted md:right-4 md:top-3"
                aria-label={t.close}
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </DrawerClose>

              <div className="grid gap-0 md:grid-cols-2 md:items-stretch md:gap-0">
                {/* Image - left on desktop, top on mobile */}
                <div className="px-4 pt-2 md:flex md:items-center md:px-6 md:py-8 md:pr-3">
                  <div className="w-full overflow-hidden rounded-2xl">
                    {activeCard.image ? (
                      <img
                        src={activeCard.image}
                        alt=""
                        className="aspect-[16/10] w-full object-cover md:aspect-[4/5] md:max-h-[min(70vh,32rem)]"
                      />
                    ) : (
                      <div
                        className={cn(
                          "aspect-[16/10] w-full md:aspect-[4/5] md:max-h-[min(70vh,32rem)]",
                          activeCard.tone,
                        )}
                      />
                    )}
                  </div>
                </div>

                {/* Content - right on desktop */}
                <div className="flex flex-col md:justify-center md:py-8 md:pl-3 md:pr-10">
                  <DrawerHeader className="gap-2 text-left md:px-4">
                    <DrawerTitle className="pr-10 text-2xl tracking-tight md:text-3xl">
                      {activeCard.name}
                    </DrawerTitle>
                    <DrawerDescription className="text-base leading-relaxed">
                      {activeCard.description}
                    </DrawerDescription>
                  </DrawerHeader>
                  <div className="px-4 pb-2 md:px-4">
                    <ul className="space-y-2.5">
                      {activeCard.highlights.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2.5 text-sm text-foreground"
                        >
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#00C853]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <DrawerFooter className="md:hidden">
                    <DrawerClose asChild>
                      <Button variant="outline" className="w-full rounded-full">
                        {t.close}
                      </Button>
                    </DrawerClose>
                  </DrawerFooter>
                </div>
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </section>
  );
}
